import { useCallback, useEffect, useState } from 'react';
import { DateRangeField } from '../components/DateRangeField';
import { cancelExpense, confirmExpense, deleteExpenseDraft, getExpense, getExpenses, getExpenseSummary } from '../api/expenses';
import { getErrorMessage } from '../api/client';
import { getStores } from '../api/stores';
import { getUsers } from '../api/users';
import { useAuth } from '../auth/AuthProvider';
import { canCancelExpense, canConfirmExpense, canCreateExpense, canCreateVoidRequest, getStoreScopeOptions } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { VoidRequestModal } from '../components/VoidRequestModal';
import { ClosedShiftNotice } from '../components/ClosedShiftNotice';
import type { ExpenseCategory, ExpenseDetail, ExpenseListFilters, ExpenseListItem, ExpensePersonRole, ExpenseShiftClose, ExpenseSortKey, ExpenseStatus, ExpenseSummary, ExpenseSummaryFilters } from '../types/expenses';
import type { Store } from '../types/stores';
import { formatMoney } from '../utils/costFormatting';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  getExpenseActions,
} from '../utils/expenseActions';
import { TicketInfoModal } from '../components/TicketCaptureModal';
import { ExpenseCaptureModal } from '../components/ExpenseCaptureModal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { WhoStack, type WhoEntry } from '../components/ui/WhoStack';
import { FilterBar, FilterQuickSelect, SortSelect, type FilterChip } from '../components/filters/FilterBar';
import { PageHeader } from '../components/ui/PageHeader';
import { SummaryStrip } from '../components/ui/SummaryStrip';
import { Icon } from '../components/ui/Icon';
import { RowToggle } from '../components/ui/RowToggle';
import { ActionBar } from '../components/ui/ActionBar';
import { MetaList } from '../components/ui/MetaList';
import { StatSummary } from '../components/ui/StatSummary';
import { Button, Field, LoadingPanel, Select } from '../components/ui/primitives';
import { useShellTier } from '../layouts/shellTier';
import { describePeriod, deviceToday } from '../utils/periods';
import { expenseStatusBadge } from '../utils/statusTone';
import { nextTableSort, sortChoices, type SortWording } from '../utils/tableSort';
import { userDisplayNameOr } from '../utils/userDisplayName';

const PAGE_SIZE = 20;
/**
 * P-075: el valor del selector de tienda que pide los gastos SIN tienda (`onlyGeneral`, que el backend
 * ya aceptaba y la pantalla nunca ofrecía). No es un id: `storeValue`/`storePatch` lo traducen.
 */
const GENERAL_STORE_VALUE = '__general__';
const GENERAL_STORE_LABEL = 'Gasto general (sin tienda)';
const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = { DRAFT: 'Borrador', CONFIRMED: 'Confirmado', CANCELLED: 'Anulado' };
const STATUS_OPTIONS = [{ value: '', label: 'Todos' }, ...(Object.entries(EXPENSE_STATUS_LABELS) as Array<[ExpenseStatus, string]>).map(([value, label]) => ({ value, label }))];
const PERSON_ROLE_LABELS: Record<Exclude<ExpensePersonRole, 'any'>, string> = { created: 'lo registró', confirmed: 'lo confirmó', cancelled: 'lo anuló' };
/** Lo que el selector de tienda muestra para unos filtros: una tienda, «sin tienda» o todas. */
const storeValue = (filters: ExpenseListFilters) => (filters.onlyGeneral ? GENERAL_STORE_VALUE : filters.storeId || '');
/** Lo que se pide al elegir en el selector de tienda: una tienda y «sin tienda» se excluyen entre sí. */
const storePatch = (value: string): Partial<ExpenseListFilters> => (value === GENERAL_STORE_VALUE
  ? { storeId: undefined, onlyGeneral: true }
  : { storeId: value || undefined, onlyGeneral: undefined });

/** Cómo se lee cada columna al ordenar. Alimenta el selector de móvil y la etiqueta del orden actual. */
const SORT_WORDING: SortWording<ExpenseSortKey> = {
  date: ['Fecha', 'más antigua primero', 'más reciente primero'],
  folio: ['Folio', 'del más bajo', 'del más alto'],
  store: ['Tienda', 'A–Z', 'Z–A'],
  // Estas tres ordenan por el valor CRUDO, que está en inglés, mientras la pantalla muestra la
  // etiqueta en español: «Estado» ascendente da Anulado, Confirmado, Borrador (CANCELLED, CONFIRMED,
  // DRAFT), que NO es alfabético en español. Por eso dicen «agrupado» y no «A–Z»: agrupar es lo que
  // de verdad hacen, y es el 90% de lo útil. Prometer un orden alfabético sería mentir.
  category: ['Categoría', 'agrupada', 'agrupada al revés'],
  concept: ['Concepto', 'A–Z', 'Z–A'],
  amount: ['Monto', 'menor primero', 'mayor primero'],
  method: ['Método de pago', 'agrupado', 'agrupado al revés'],
  status: ['Estado', 'agrupado', 'agrupado al revés'],
  who: ['Quién lo registró', 'A–Z', 'Z–A'],
};
/** Los órdenes que se ofrecen de un toque. Corta a propósito: las 18 combinaciones serían una pared. */
const SORT_CHOICES = [
  ['date', 'desc'], ['date', 'asc'], ['amount', 'desc'], ['amount', 'asc'],
  ['concept', 'asc'], ['store', 'asc'], ['category', 'asc'], ['status', 'asc'], ['who', 'asc'],
] as const;

/**
 * Los filtros del RESUMEN se derivan de los de la LISTA quitando lo que no cambia una suma: el orden
 * y la paginación. Derivarlos —en vez de escribirlos a mano— es lo que impide que las tarjetas de
 * dinero de arriba vuelvan a describir un conjunto distinto del que muestra la tabla.
 */
function summaryFiltersOf(filters: ExpenseListFilters): ExpenseSummaryFilters {
  const { sort: _sort, dir: _dir, limit: _limit, offset: _offset, ...rest } = filters;
  return rest;
}
const MIN_CANCEL_REASON_LENGTH = 10;
const CATEGORY_OPTIONS = Object.entries(EXPENSE_CATEGORY_LABELS) as Array<[ExpenseCategory, string]>;

export function ExpensesPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [stores, setStores] = useState<Store[]>([]);
  /**
   * DOS estados de filtro, no uno. Antes cada select escribía directo en el filtro aplicado y la
   * paginación leía ese mismo objeto: cambiar «Categoría» y pulsar «Siguiente» sin aplicar pedía la
   * página con el filtro NUEVO y el desplazamiento VIEJO, saltándose filas sin avisar. `draftFilters`
   * es lo que el dueño está escribiendo; `filters` es lo que la tabla está mostrando de verdad.
   */
  const [draftFilters, setDraftFilters] = useState<ExpenseListFilters>({});
  const [filters, setFilters] = useState<ExpenseListFilters>({});
  /**
   * Personas para el filtro «Quién». Solo un dueño o gerente puede listar usuarios (getUsers exige
   * ser administrador), así que a un empleado la lista le llega vacía y el control no se dibuja: no
   * es un permiso nuevo, es el que ya existe.
   */
  const [people, setPeople] = useState<Array<{ id: string; label: string }>>([]);
  /** El texto que se está escribiendo en el buscador (se aplica al enviarlo, sobre lo aplicado). */
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState<ExpenseSortKey>('date');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [items, setItems] = useState<ExpenseListItem[]>([]);
  const [page, setPage] = useState({ total: 0, offset: 0, hasMore: false });
  const [summary, setSummary] = useState<ExpenseSummary>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [formModalOpen, setFormModalOpen] = useState<'new' | string | undefined>();
  // Deep-link #expenses/<id> ("Ver registro" desde Solicitudes): abre el detalle directo.
  const [detailId, setDetailIdState] = useState<string | undefined>(() => window.location.hash.replace('#', '').split('/')[1] || undefined);
  const setDetailId = (id: string | undefined) => { setDetailIdState(id); if (id === undefined && window.location.hash.startsWith('#expenses/')) window.location.hash = 'expenses'; };
  // Ticket Mixto: consulta del ticket de origen desde un gasto "De ticket".
  const [ticketInfoId, setTicketInfoId] = useState<string>();

  const load = useCallback(async (rawFilters: ExpenseListFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    // Un buscador con SOLO espacios llegaba al backend como texto vacío, y su esquema exige al menos
    // un carácter: la pantalla entera se caía con «La información enviada no es válida» por haber
    // pulsado la barra espaciadora. Se normaliza aquí, el único sitio por el que pasan todas las
    // cargas (aplicar, ordenar, paginar, limpiar), y no en el onChange del campo: recortar mientras
    // se teclea impediría escribir el espacio entre dos palabras.
    const buscado = rawFilters.search?.trim();
    // `sort` y `dir` solo viajan si NO son los de por defecto. Los esquemas del backend son
    // `.strict()` y un servidor anterior rechaza con 400 cualquier parámetro que no conozca; Render
    // publica backend y web como servicios distintos, así que existe una ventana de unos minutos con
    // la web ya nueva y el servidor todavía viejo. Omitirlos cuando valen exactamente lo que el
    // backend usaría de todos modos deja esa ventana sin efecto para el uso normal de la pantalla:
    // solo pedir un orden DISTINTO al de siempre fallaría, y basta con reintentar al terminar.
    const ordenPorDefecto = (rawFilters.sort ?? 'date') === 'date' && (rawFilters.dir ?? 'desc') === 'desc';
    const nextFilters: ExpenseListFilters = {
      ...rawFilters,
      search: buscado || undefined,
      sort: ordenPorDefecto ? undefined : rawFilters.sort,
      dir: ordenPorDefecto ? undefined : rawFilters.dir,
    };
    try {
      const [listResult, summaryResult] = await Promise.all([
        getExpenses({ ...nextFilters, limit: PAGE_SIZE, offset }),
        // Las MISMAS condiciones que la lista. Antes solo se le pasaban tienda, categoría y fechas,
        // así que filtrar por estado —o buscar— dejaba arriba el total del periodo entero y abajo
        // otra cosa. El dueño veía dos números y ninguno explicaba al otro.
        getExpenseSummary(summaryFiltersOf(nextFilters)),
      ]);
      setFilters(nextFilters);
      setItems(listResult.items);
      setPage({ total: listResult.total, offset: listResult.offset, hasMore: listResult.hasMore });
      setSummary(summaryResult);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar Gastos.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void getStores().then((result) => {
      setStores(result.stores);
      const { defaultStoreId } = getStoreScopeOptions(user?.role, result.stores);
      const initialFilters: ExpenseListFilters = defaultStoreId ? { storeId: defaultStoreId } : {};
      setDraftFilters(initialFilters);
      void load(initialFilters);
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas.')));
    // Las personas van aparte y con su propio catch: solo un administrador puede listarlas, y que un
    // empleado no pueda no debe impedirle ver sus gastos. Sin lista, el filtro no se dibuja.
    void getUsers()
      .then((result) => setPeople(result.users.filter((person) => person.isActive).map((person) => ({ id: person.id, label: userDisplayNameOr(person) }))))
      .catch(() => setPeople([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Si el dueño acotó fechas a mano, esta es la salida visible cuando no encuentra lo que busca. */
  const hasDateWindow = Boolean(filters.dateFrom || filters.dateTo);
  /**
   * La tienda que el ROL impone. Un gerente o un empleado no pueden ver «todas»: el selector no les
   * ofrece la opción vacía y el backend responde 400 EXPENSE_LIST_STORE_REQUIRED sin ella. Por eso
   * esa tienda NO es un filtro que el dueño haya puesto, y tratarla como tal rompía dos cosas:
   * «Limpiar» la borraba y tumbaba la pantalla, y el panel vacío acusaba a un filtro inexistente
   * («ningún gasto coincide con lo que buscas») el día que una sucursal todavía no tenía gastos.
   */
  const { allowsAllStores, defaultStoreId: forcedStoreId } = getStoreScopeOptions(user?.role, stores);
  const baseFilters: ExpenseListFilters = forcedStoreId ? { storeId: forcedStoreId } : {};
  const storeChosenByUser = Boolean(filters.storeId) && filters.storeId !== forcedStoreId;
  const hasActiveFilters = Boolean(filters.dateFrom || filters.dateTo || storeChosenByUser || filters.onlyGeneral || filters.category || filters.status || filters.search || filters.personId);
  const clearFilters = () => {
    // Vuelve al estado con el que ARRANCA la pantalla, que para un gerente o un empleado incluye su
    // tienda: mandar un objeto vacío les dejaba el selector en blanco y la pantalla en error 400.
    // El orden también vuelve a su valor de origen: dejarlo puesto haría que la flecha de la
    // cabecera dijera una cosa y el servidor devolviera otra.
    const limpio: ExpenseListFilters = { ...baseFilters };
    setDraftFilters(limpio);
    setSearchText('');
    setSort('date');
    setDir('desc');
    void load(limpio, 0);
  };
  /** Repite la MISMA búsqueda sin el rango de fechas: el caso «sé que existe y no aparece». */
  const searchWholeHistory = () => {
    // Conserva el orden elegido: quitar las fechas no debería reordenar la tabla por sorpresa.
    // P-075: parte de lo APLICADO (antes, del borrador del formulario, que ya no existe fuera de «Filtros»).
    void load({ ...filters, sort, dir, dateFrom: undefined, dateTo: undefined }, 0);
  };

  /**
   * P-075 · La barra de filtros (mismo contrato que Ventas e ingresos). Lo aplicado vive en `filters`. La
   * búsqueda, el periodo, los rápidos y cada «×» se aplican SOBRE lo aplicado: nunca arrastran un control
   * que todavía está en borrador. Los demás se eligen en borrador en «Filtros» (`draftFilters`, que se copia
   * de lo aplicado al abrirlo) y se piden con «Ver resultados». El orden se conserva siempre.
   */
  const applyPatch = (patch: Partial<ExpenseListFilters>) => { if (!isLoading) void load({ ...filters, ...patch, sort, dir }, 0); };
  const panelCount = (storeChosenByUser || filters.onlyGeneral ? 1 : 0) + (filters.category ? 1 : 0) + (filters.status ? 1 : 0) + (filters.personId ? 1 : 0);
  const storeNameOf = (id?: string) => stores.find((store) => store.id === id)?.name ?? 'elegida';
  const personChip = () => {
    const name = people.find((person) => person.id === filters.personId)?.label ?? 'elegida';
    const role = filters.personRole && filters.personRole !== 'any' ? ` (${PERSON_ROLE_LABELS[filters.personRole]})` : '';
    return `Quién: ${name}${role}`;
  };
  const chips: FilterChip[] = [
    ...(filters.search ? [{ id: 'search', label: `Búsqueda: «${filters.search}»`, onRemove: () => { setSearchText(''); applyPatch({ search: undefined }); } }] : []),
    ...(hasDateWindow ? [{ id: 'dates', label: `Fechas: ${describePeriod({ from: filters.dateFrom ?? '', to: filters.dateTo ?? '' }, deviceToday())}`, onRemove: () => applyPatch({ dateFrom: undefined, dateTo: undefined }) }] : []),
    ...(storeChosenByUser ? [{ id: 'store', label: `Tienda: ${storeNameOf(filters.storeId)}`, onRemove: () => applyPatch({ storeId: forcedStoreId }) }] : []),
    ...(filters.onlyGeneral ? [{ id: 'general', label: `Tienda: ${GENERAL_STORE_LABEL}`, onRemove: () => applyPatch({ onlyGeneral: undefined, storeId: forcedStoreId }) }] : []),
    ...(filters.category ? [{ id: 'category', label: `Categoría: ${EXPENSE_CATEGORY_LABELS[filters.category]}`, onRemove: () => applyPatch({ category: undefined }) }] : []),
    ...(filters.status ? [{ id: 'status', label: `Estado: ${EXPENSE_STATUS_LABELS[filters.status]}`, onRemove: () => applyPatch({ status: undefined }) }] : []),
    ...(filters.personId ? [{ id: 'person', label: personChip(), onRemove: () => applyPatch({ personId: undefined, personRole: undefined }) }] : []),
  ];
  // «Todas las tiendas» y «Gasto general (sin tienda)» solo para quien puede ver todas (el dueño): a un
  // gerente el backend le exige una tienda, y a un empleado le ignora `onlyGeneral` por completo.
  const storeOptions = [
    ...(allowsAllStores ? [{ value: '', label: 'Todas las tiendas' }] : []),
    ...stores.map((store) => ({ value: store.id, label: store.name })),
    ...(allowsAllStores ? [{ value: GENERAL_STORE_VALUE, label: GENERAL_STORE_LABEL }] : []),
  ];

  /**
   * El orden vive en el SERVIDOR. La lista pide 20 filas por página: ordenar aquí reordenaría solo
   * lo visible y diría «el gasto más caro» cuando en realidad es «el más caro de estos veinte».
   *
   * Por eso cada clic vuelve a pedir la página Y vuelve a la primera: reordenar estando en la página
   * 3 dejaría al dueño mirando un tramo arbitrario del orden nuevo.
   */
  const sortBy = (key: ExpenseSortKey) => {
    const next = nextTableSort({ key: sort, dir }, key);
    setSort(next.key);
    setDir(next.dir);
    void load({ ...filters, sort: next.key, dir: next.dir }, 0);
  };
  const th = (key: ExpenseSortKey, label: string) => {
    const active = sort === key;
    return <th aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button className="data-table__sort" onClick={() => sortBy(key)} type="button">{label}
        <span aria-hidden="true" className={active ? 'data-table__sort-arrow data-table__sort-arrow--active' : 'data-table__sort-arrow'}>{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>;
  };

  const refreshAfterChange = async (message: string) => {
    setNotice(message);
    await load(filters, page.offset);
  };

  const handleDeleteDraft = async (expense: Pick<ExpenseListItem, 'id'>) => {
    if (!(await confirm({ title: 'Eliminar borrador', description: 'Esta acción no afecta reportes confirmados. ¿Eliminar este borrador?', confirmLabel: 'Eliminar', danger: true }))) return;
    setError(undefined);
    try {
      await deleteExpenseDraft(expense.id);
      if (detailId === expense.id) setDetailId(undefined);
      await refreshAfterChange('Borrador eliminado.');
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'No fue posible eliminar el borrador.'));
    }
  };

  const handleConfirm = async (expense: Pick<ExpenseListItem, 'id'>) => {
    setError(undefined);
    try {
      await confirmExpense(expense.id);
      await refreshAfterChange('Gasto confirmado correctamente.');
    } catch (confirmError) {
      setError(getErrorMessage(confirmError, 'No fue posible confirmar el gasto.'));
    }
  };

  const panelFields = <>
    <Field label="Tienda">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, ...storePatch(event.target.value) })} value={storeValue(draftFilters)}>
        {storeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
    <Field label="Categoría">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, category: (event.target.value || undefined) as ExpenseCategory | undefined })} value={draftFilters.category || ''}>
        <option value="">Todas</option>{CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </Select>
    </Field>
    <Field label="Estado">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, status: (event.target.value || undefined) as ExpenseStatus | undefined })} value={draftFilters.status || ''}>
        {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
    {/* «Quién» solo si hay personas: listarlas exige ser administrador, así que a un empleado no le llega
        la lista y el control no se dibuja (no es un permiso nuevo). */}
    {people.length > 0 && <Field label="Quién">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, personId: event.target.value || undefined })} value={draftFilters.personId || ''}>
        <option value="">Cualquiera</option>
        {people.map((person) => <option key={person.id} value={person.id}>{person.label}</option>)}
      </Select>
    </Field>}
    {/* Tres papeles, no cuatro: un gasto no pasa por aprobación, así que no hay «lo envió». */}
    {people.length > 0 && draftFilters.personId && <Field label="En qué papel">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, personRole: event.target.value as ExpensePersonRole })} value={draftFilters.personRole || 'any'}>
        <option value="any">Cualquier papel</option>
        <option value="created">Lo registró</option>
        <option value="confirmed">Lo confirmó</option>
        <option value="cancelled">Lo anuló</option>
      </Select>
    </Field>}
  </>;

  return <div className="n3-screen expenses-page">
    {/* P-075: un solo encabezado (antes «Gastos» + «Historial de gastos») y la acción principal en él. */}
    <PageHeader
      actions={canCreateExpense(user?.role)
        ? <Button iconStart={<Icon name="plus" size={18} />} onClick={() => setFormModalOpen('new')} variant="primary">Registrar gasto</Button>
        : undefined}
      description="Lo que sale para operar: renta, sueldos, servicios, empaques. Las compras de insumos van en Compras de insumos."
      title="Gastos"
    />
    {error && <div className="alert alert--error">{error}</div>}
    {notice && <div className="alert alert--success">{notice}</div>}

    {/* Una cifra a la vista y el resto en el desglose: los mismos tres totales, con los mismos filtros que
        la lista. El anulado va atenuado: es dinero que ya NO cuenta. */}
    {summary && <SummaryStrip
      details={[
        { label: 'Total borrador', value: formatMoney(summary.draftTotal) },
        { label: 'Total anulado', value: formatMoney(summary.cancelledTotal), muted: true },
      ]}
      label="Totales de Gastos"
      primary={{ label: 'Total confirmado', value: formatMoney(summary.confirmedTotal) }}
    />}

    {/* El proveedor y el empleado de un sueldo se guardan los dos en `vendorName`, así que el buscador es
        el que contesta «¿cuánto le pagué a Juan?». maxLength = el tope del esquema del backend (120): pegar
        un texto más largo no puede devolver 400. */}
    <FilterBar
      busy={isLoading}
      chips={chips}
      label="Buscar y filtrar gastos"
      onClear={hasActiveFilters ? clearFilters : undefined}
      panel={{
        applying: isLoading,
        content: panelFields,
        count: panelCount,
        onApply: () => { if (!isLoading) void load({ ...draftFilters, search: filters.search, dateFrom: filters.dateFrom, dateTo: filters.dateTo, sort, dir }, 0); },
        onOpen: () => setDraftFilters({ ...filters }),
        onReset: () => setDraftFilters({ ...baseFilters }),
      }}
      period={<DateRangeField disabled={isLoading} from={filters.dateFrom || ''} onChange={(from, to) => applyPatch({ dateFrom: from || undefined, dateTo: to || undefined })} to={filters.dateTo || ''} />}
      quick={<>
        {stores.length > 1 && <FilterQuickSelect disabled={isLoading} label="Tienda" onChange={(value) => applyPatch(storePatch(value))} options={storeOptions} value={storeValue(filters)} />}
        <FilterQuickSelect disabled={isLoading} label="Estado" onChange={(value) => applyPatch({ status: (value || undefined) as ExpenseStatus | undefined })} options={STATUS_OPTIONS} value={filters.status || ''} />
      </>}
      search={{ label: 'Buscar gastos', maxLength: 120, onChange: setSearchText, onSubmit: (text) => applyPatch({ search: text || undefined }), placeholder: 'Folio, concepto, proveedor o nota', value: searchText }}
      sort={<SortSelect
        disabled={isLoading}
        onChange={(value) => { const [key, nextDir] = value.split(':') as [ExpenseSortKey, 'asc' | 'desc']; setSort(key); setDir(nextDir); void load({ ...filters, sort: key, dir: nextDir }, 0); }}
        options={sortChoices(SORT_WORDING, SORT_CHOICES, { key: sort, dir })}
        value={`${sort}:${dir}`}
      />}
      summary={`${page.total} ${page.total === 1 ? 'gasto' : 'gastos'}${hasActiveFilters ? ' en lo que estás viendo' : ''}`}
    />

    {isLoading ? <LoadingPanel label="Cargando gastos…" /> : <ExpensesTable items={items} onOpen={setDetailId} onConfirm={handleConfirm} onDeleteDraft={handleDeleteDraft} onOpenTicket={setTicketInfoId} stores={stores} th={th} hasActiveFilters={hasActiveFilters} hasDateWindow={hasDateWindow} onClearFilters={clearFilters} onSearchWholeHistory={searchWholeHistory} />}
    {ticketInfoId && <TicketInfoModal ticketId={ticketInfoId} onClose={() => setTicketInfoId(undefined)} />}
    {!isLoading && page.total > 0 && <div className="coverage-pagination">
      <span>Mostrando {page.offset + 1}–{page.offset + items.length} de {page.total}</span>
      <div>
        <button className="button button--ghost" disabled={page.offset === 0} onClick={() => void load(filters, Math.max(0, page.offset - PAGE_SIZE))} type="button">Anterior</button>
        <button className="button button--ghost" disabled={!page.hasMore} onClick={() => void load(filters, page.offset + PAGE_SIZE)} type="button">Siguiente</button>
      </div>
    </div>}

    {formModalOpen && <ExpenseCaptureModal
      expenseId={formModalOpen === 'new' ? undefined : formModalOpen}
      stores={stores}
      onClose={() => setFormModalOpen(undefined)}
      onSaved={async (message) => { setFormModalOpen(undefined); await refreshAfterChange(message); }}
    />}

    {detailId && <ExpenseDetailModal
      expenseId={detailId}
      stores={stores}
      onClose={() => setDetailId(undefined)}
      onEdit={() => setFormModalOpen(detailId)}
      onDeleteDraft={() => handleDeleteDraft({ id: detailId })}
      onChanged={async (message) => { setDetailId(undefined); await refreshAfterChange(message); }}
    />}
  </div>;
}

function ExpensesTable({ items, onOpen, onConfirm, onDeleteDraft, onOpenTicket, stores, th, hasActiveFilters, hasDateWindow, onClearFilters, onSearchWholeHistory }: {
  items: ExpenseListItem[];
  onOpen: (id: string) => void;
  onConfirm: (expense: Pick<ExpenseListItem, 'id'>) => Promise<void>;
  onDeleteDraft: (expense: Pick<ExpenseListItem, 'id'>) => Promise<void>;
  onOpenTicket: (ticketId: string) => void;
  stores: Store[];
  th: (key: ExpenseSortKey, label: string) => JSX.Element;
  hasActiveFilters: boolean;
  hasDateWindow: boolean;
  onClearFilters: () => void;
  onSearchWholeHistory: () => void;
}) {
  const { user } = useAuth();
  // P-075: la tienda bajo el folio solo hace falta en la tarjeta del teléfono (≤ 767 px, el mismo corte
  // que `compact`). En escritorio la columna «Tienda» ya la dice y se leía dos veces en la misma fila.
  const cardLayout = useShellTier() === 'compact';
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  // Un panel vacío que no distingue «todavía no hay gastos» de «ninguno coincide con lo que buscas»
  // deja al dueño sin saber si el dato no existe o si es su filtro el que lo esconde.
  if (!items.length) return <div className="empty-panel">{hasActiveFilters
    ? <><strong>Ningún gasto coincide con lo que buscas</strong><span>{hasDateWindow ? 'La pantalla está mirando solo un rango de fechas. Puede que el gasto exista fuera de él.' : 'Prueba con otro texto, o quita algún filtro.'}</span><div className="empty-panel__actions">{hasDateWindow && <button className="button button--secondary" onClick={onSearchWholeHistory} type="button">Buscar en todo el historial</button>}<button className="text-button" onClick={onClearFilters} type="button">Quitar todos los filtros</button></div></>
    : <><strong>Sin gastos registrados</strong><span>Registra un gasto operativo para comenzar.</span></>}</div>;
  // Móvil (≤640px): data-table--cards convierte cada gasto en tarjeta colapsable (mismo patrón que cortes).
  return <div className="data-table-wrap"><table className="data-table expenses-table expenses-table--who data-table--cards n3-has-row-toggle"><thead><tr>{th('date', 'Folio · Fecha')}{th('store', 'Tienda')}{th('category', 'Categoría')}{th('concept', 'Concepto')}{th('amount', 'Monto')}{th('method', 'Método de pago')}{th('status', 'Estado')}{th('who', 'Quién')}{/* «Acciones» son botones, no un dato: no se ordena. */}<th>Acciones</th></tr></thead><tbody>{items.map((item) => {
    const actions = getExpenseActions(item.status);
    const timeZone = stores.find((store) => store.id === item.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;
    return <tr className={`${item.status === 'CANCELLED' ? 'data-row--inactive' : item.hasPendingVoidRequest || item.hasApprovedChangeRequest ? 'data-row--flagged' : ''} ${expandedIds.has(item.id) ? 'dt-card--open' : 'dt-card--closed'}`} onClick={() => toggleExpanded(item.id)} key={item.id}>
      <td className="dt-card-main"><strong>{item.folio ? `${item.folio} · ` : ''}{formatExpenseDate(item.expenseDate, timeZone)}</strong>{cardLayout && <small>{item.storeName || 'Gasto general'}</small>}<RowToggle expanded={expandedIds.has(item.id)} label={item.folio || item.concept} onToggle={() => toggleExpanded(item.id)} /></td>
      <td data-label="Tienda">{item.storeName || 'Gasto general'}</td>
      <td data-label="Categoría">{EXPENSE_CATEGORY_LABELS[item.category]}</td>
      <td className="dt-card-keep" data-label="Concepto">{item.concept}</td>
      <td className="dt-card-keep" data-label="Monto"><strong>{formatMoney(item.amount, item.currency)}</strong></td>
      <td data-label="Método de pago">{item.paymentMethod ? EXPENSE_PAYMENT_METHOD_LABELS[item.paymentMethod] : 'Sin especificar'}</td>
      <td className="dt-card-keep" data-label="Estado"><ExpenseStatusPill status={item.status} /><ShiftCloseChip storeId={item.storeId} shiftClose={item.shiftClose} timeZone={timeZone} />{item.ticketCaptureId && <button className="consumption-status ticket-chip" onClick={(event) => { event.stopPropagation(); onOpenTicket(item.ticketCaptureId!); }} title="Este gasto nació de una captura única de ticket" type="button">De ticket</button>}{item.status !== 'CANCELLED' && item.hasPendingVoidRequest && <span className="consumption-status consumption-status--pending">Solicitud pendiente</span>}{item.status !== 'CANCELLED' && item.hasApprovedChangeRequest && <span className="consumption-status consumption-status--pending">Cambio aprobado — aplicar</span>}</td>
      {/* El dato ya viajaba en cada gasto de la lista y solo se veía dentro del detalle. Mismo
          componente y mismo vocabulario que Compras, para no llamar a lo mismo de dos maneras. */}
      <td className="dt-card-keep who-cell" data-label="Quién"><WhoStack entries={expenseWhoEntries(item)} /></td>
      <td className="dt-card-actions dt-card-keep"><div className="table-actions">
        <button className="text-button" onClick={(event) => { event.stopPropagation(); onOpen(item.id); }} type="button">Ver detalle</button>
        {actions.canConfirm && canConfirmExpense(user?.role) && <button className="text-button" onClick={(event) => { event.stopPropagation(); void onConfirm(item); }} type="button">Confirmar</button>}
        {actions.canDeleteDraft && <button className="text-button text-button--danger" onClick={(event) => { event.stopPropagation(); void onDeleteDraft(item); }} type="button">Eliminar</button>}
      </div></td>
    </tr>;
  })}</tbody></table></div>;
}

function ExpenseDetailModal({ expenseId, stores, onClose, onEdit, onDeleteDraft, onChanged }: {
  expenseId: string;
  stores: Store[];
  onClose: () => void;
  onEdit: () => void;
  onDeleteDraft: () => Promise<void>;
  onChanged: (message: string) => Promise<void>;
}) {
  const { user } = useAuth();
  const [detail, setDetail] = useState<ExpenseDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isVoidRequestModalOpen, setIsVoidRequestModalOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getExpense(expenseId);
      setDetail(result.expense);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el detalle del gasto.'));
    } finally {
      setIsLoading(false);
    }
  }, [expenseId]);

  useEffect(() => { void load(); }, [load]);

  if (!detail && isLoading) return <Modal title="Detalle del gasto" onClose={onClose}><div className="inline-loading"><span className="spinner" />Cargando detalle...</div></Modal>;
  if (!detail) return <Modal title="Detalle del gasto" onClose={onClose}>{error && <div className="alert alert--error">{error}</div>}</Modal>;

  const actions = getExpenseActions(detail.status);
  const timeZone = stores.find((store) => store.id === detail.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;

  // P-076: las mismas acciones, en el mismo sitio de siempre (el pie de la hoja, siempre a la vista) y con las mismas
  // condiciones; lo destructivo (eliminar el borrador, anular) va aparte, en texto rojo, y el motivo en su renglón.
  // Codex R2 · H1: en una primera versión bajaron al final del cuerpo; volvieron al pie, con «Cerrar».
  const footer = (
    <ActionBar
      aria-label="Acciones del gasto"
      danger={(actions.canDeleteDraft || (actions.canCancel && canCancelExpense(user?.role))) ? <>
        {actions.canDeleteDraft && <Button onClick={() => void onDeleteDraft()} variant="danger">Eliminar borrador</Button>}
        {actions.canCancel && canCancelExpense(user?.role) && <Button onClick={() => setIsCancelModalOpen(true)} variant="danger">Anular gasto</Button>}
      </> : undefined}
      note={detail.status === 'CANCELLED' ? 'Este gasto ya fue anulado.'
        : actions.canCancel && detail.hasPendingVoidRequest ? 'Ya solicitaste un cambio de este gasto — está pendiente de revisión.'
          : actions.canCancel && !detail.hasPendingVoidRequest && !canCancelExpense(user?.role) && (!canCreateVoidRequest(user?.role) || detail.createdByUserId !== user?.id) ? 'No tienes permiso para anular gastos.'
            : undefined}
    >
      <Button onClick={onClose} variant="ghost">Cerrar</Button>
      {actions.canEdit && <Button onClick={onEdit} variant="secondary">Editar</Button>}
      {/* P-075 (C2): el botón se llama como el diálogo que abre y como la bandeja («anulación o cambio»). */}
      {actions.canCancel && !canCancelExpense(user?.role) && canCreateVoidRequest(user?.role) && detail.createdByUserId === user?.id && !detail.hasPendingVoidRequest
        && <Button onClick={() => setIsVoidRequestModalOpen(true)} variant="secondary">Solicitar anulación o cambio</Button>}
    </ActionBar>
  );

  // El folio va en el encabezado: estando dentro del modal, identificar el gasto no debería obligar a
  // salir a la lista ni a deducirlo por la fecha.
  return <Modal title={detail.folio ? `Gasto ${detail.folio}` : 'Detalle del gasto'} description={detail.concept} footer={footer} onClose={onClose}>
    {error && <div className="alert alert--error">{error}</div>}
    {/* P-076: primero lo que se viene a ver (total, estado, método, factura); después cada dato con su
        etiqueta, en lista, igual que el detalle de la venta. Son los mismos datos de antes. */}
    <StatSummary
      label="Resumen del gasto"
      stats={[
        { label: 'Total', value: formatMoney(detail.amount, detail.currency) },
        { label: 'Estado', value: <ExpenseStatusPill status={detail.status} /> },
        { label: 'Método de pago', value: detail.paymentMethod ? EXPENSE_PAYMENT_METHOD_LABELS[detail.paymentMethod] : 'Sin especificar' },
        { label: '¿Con factura?', value: detail.isInvoiced ? 'Sí' : 'No' },
      ]}
    />
    <MetaList
      aria-label="Datos del gasto"
      columns
      items={[
        { label: 'Concepto', value: detail.concept, emphasis: true },
        { label: 'Fecha', value: formatExpenseDate(detail.expenseDate, timeZone) },
        { label: 'Tienda', value: detail.storeName || 'Gasto general' },
        { label: 'Corte de turno', value: detail.storeId
          ? (detail.shiftClose
            ? <>Ya contado en <strong>{detail.shiftClose.folio ?? 'un corte cerrado'}</strong> <small>(cerrado el {formatExpenseDate(detail.shiftClose.closedAt, timeZone)}{detail.shiftClose.employeeName ? ` por ${detail.shiftClose.employeeName}` : ''})</small></>
            : 'Sin corte todavía')
          : 'No aplica — es un gasto general' },
        { label: 'Categoría', value: EXPENSE_CATEGORY_LABELS[detail.category] },
        ...(detail.subcategory ? [{ label: 'Subcategoría', value: detail.subcategory }] : []),
        { label: 'Subtotal', value: detail.subtotalAmount ? formatMoney(detail.subtotalAmount, detail.currency) : formatMoney(detail.amount, detail.currency) },
        { label: `IVA (${detail.taxPercentage ? Number(detail.taxPercentage).toFixed(1) : '0.0'}%)`, value: detail.taxAmount ? formatMoney(detail.taxAmount, detail.currency) : formatMoney('0.00', detail.currency) },
        { label: 'Proveedor', value: detail.vendorName || 'Sin proveedor' },
        { label: 'Folio de la factura', value: detail.reference || 'Sin folio' },
        { label: 'Registrado por', value: detail.createdByName || 'Sin usuario' },
        ...(detail.confirmedAt ? [{ label: 'Confirmado', value: `${formatExpenseDate(detail.confirmedAt, timeZone)} por ${detail.confirmedByName || 'Sin usuario'}` }] : []),
      ]}
    />
    {/* Notas y anulación: a la vista, con la etiqueta encima (son textos largos). */}
    {(detail.notes || detail.cancelledAt || detail.cancellationReason) && <MetaList
      aria-label="Notas y anulación"
      items={[
        ...(detail.notes ? [{ label: 'Notas', value: detail.notes }] : []),
        ...(detail.cancelledAt ? [{ label: 'Anulado', value: `${formatExpenseDate(detail.cancelledAt, timeZone)} por ${detail.cancelledByName || 'Sin usuario'}` }] : []),
        ...(detail.cancellationReason ? [{ label: 'Motivo de anulación', value: detail.cancellationReason }] : []),
      ]}
      stack
    />}

    {/* Historial: anular siempre dejó rastro; modificar no dejaba ninguno. Sólo aparece si hubo
        cambios — un bloque vacío que dice "sin cambios" no le sirve a nadie. */}
    {detail.history && detail.history.length > 0 && <section className="change-history">
      <h4>Historial de cambios</h4>
      <ul>{detail.history.map((change, index) => <li key={index}>
        <div className="change-history__what">
          <strong>{change.fieldLabel}</strong>
          <span className="change-history__values">
            <span className="change-history__old">{change.oldValue ?? 'vacío'}</span>
            <span className="change-history__arrow" aria-label="cambió a"> → </span>
            <span className="change-history__new">{change.newValue ?? 'vacío'}</span>
          </span>
        </div>
        <small>{formatExpenseDate(change.changedAt, timeZone)}{change.changedByName ? ` · ${change.changedByName}` : ''}</small>
      </li>)}</ul>
    </section>}



    {isCancelModalOpen && <CancelExpenseModal
      expense={detail}
      timeZone={timeZone}
      onClose={() => setIsCancelModalOpen(false)}
      onCancelled={() => { setIsCancelModalOpen(false); void onChanged('Gasto anulado correctamente.'); }}
    />}

    {isVoidRequestModalOpen && <VoidRequestModal
      targetType="OTHER_EXPENSE"
      targetId={detail.id}
      targetSummary={`Gasto: ${detail.concept} — ${formatMoney(detail.amount, detail.currency)}`}
      storeId={detail.storeId}
      occurredAt={detail.expenseDate}
      timeZone={timeZone}
      onClose={() => setIsVoidRequestModalOpen(false)}
      onSubmitted={async (message) => { setIsVoidRequestModalOpen(false); await onChanged(message); }}
    />}
  </Modal>;
}

function CancelExpenseModal({ expense, timeZone, onClose, onCancelled }: { expense: ExpenseDetail; timeZone: string; onClose: () => void; onCancelled: () => void }) {
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const canConfirm = reason.trim().length >= MIN_CANCEL_REASON_LENGTH;

  const confirmCancel = async () => {
    if (!canConfirm || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await cancelExpense(expense.id, { confirm: true, reason: reason.trim() });
      onCancelled();
    } catch (cancelError) {
      setError(getErrorMessage(cancelError, 'No fue posible anular este gasto.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Anular gasto"
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isSaving || !canConfirm} onClick={() => void confirmCancel()} type="button">{isSaving ? 'Anulando...' : 'Anular gasto'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    {/* Si este gasto ya lo contó un corte cerrado, decirlo ANTES de anular — no después. */}
    <ClosedShiftNotice storeId={expense.storeId} at={expense.expenseDate} timeZone={timeZone} action="anular" />
    <dl className="detail-list">
      <div><dt>Tienda</dt><dd>{expense.storeName || 'Gasto general'}</dd></div>
      <div><dt>Concepto</dt><dd>{expense.concept}</dd></div>
      <div><dt>Monto</dt><dd>{formatMoney(expense.amount, expense.currency)}</dd></div>
    </dl>
    <div className="alert alert--warning">Este gasto no se borrará del historial. Se marcará como anulado y dejará de contarse como gasto activo.</div>
    <label className="field">
      <span>Motivo de la anulación (mínimo {MIN_CANCEL_REASON_LENGTH} caracteres)</span>
      <textarea onChange={(event) => setReason(event.target.value)} rows={3} value={reason} />
    </label>
  </Modal>;
}

// Ola 4 (D6): estado→StatusBadge canónico (mapeo compartido en utils/statusTone).
/**
   * Dice de un vistazo si el gasto ya entró en un corte de turno CERRADO. Es un dato, no una alerta:
   * por eso es neutro y no rojo. Un gasto general (sin tienda) nunca entra en el corte de una tienda,
   * así que ahí no se dibuja nada en vez de mentir con un "sin corte".
   */
function ShiftCloseChip({ storeId, shiftClose, timeZone }: { storeId: string | null; shiftClose?: ExpenseShiftClose | null; timeZone: string }) {
  if (!storeId) return null;
  if (shiftClose) {
    return <span
      className="consumption-status consumption-status--in-shift"
      title={`Turno cerrado el ${formatExpenseDate(shiftClose.closedAt, timeZone)}${shiftClose.employeeName ? ` por ${shiftClose.employeeName}` : ''}. Anularlo no rehace ese corte.`}
    >En el corte {shiftClose.folio ?? 'cerrado'}</span>;
  }
  return <span className="consumption-status consumption-status--no-shift" title="Todavía no hay un corte de turno cerrado que cubra la fecha de este gasto.">Sin corte todavía</span>;
}

function ExpenseStatusPill({ status }: { status: ExpenseStatus }) {
  const view = expenseStatusBadge(status);
  return <StatusBadge variant={view.variant}>{view.label}</StatusBadge>;
}

/**
 * Quién registró el gasto y quién lo resolvió, para la columna «Quién» de la lista. Gemelo de
 * `purchaseWhoEntries` en PurchasingPanel; el verbo sigue al estado y usa el vocabulario que este
 * módulo ya emplea en el detalle: «Confirmado» y «Anulado» (no «Aprobó»/«Canceló», que son de
 * compras — un gasto no pasa por aprobación).
 *   CONFIRMADO → Confirmó   ·   ANULADO → Anuló   ·   BORRADOR → (nada: nadie lo ha resuelto)
 */
function expenseWhoEntries(item: ExpenseListItem): WhoEntry[] {
  const entries: WhoEntry[] = [{ label: 'Registró', name: item.createdByName }];
  if (item.status === 'CANCELLED') entries.push({ label: 'Anuló', name: item.cancelledByName });
  else if (item.status === 'CONFIRMED') entries.push({ label: 'Confirmó', name: item.confirmedByName });
  return entries;
}

function formatExpenseDate(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) {
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value));
}
